import { logger } from "./logger";

export interface EnhancedWebSocketOptions {
  url: string;
  onMessage: (event: MessageEvent) => void;
  onError: (error: Error) => void;
  onConnectionChange: (connected: boolean) => void;
  onOpen?: () => void;
  maxReconnectAttempts?: number;
  initialReconnectDelay?: number;
  maxReconnectDelay?: number;
  heartbeatInterval?: number;
  heartbeatTimeout?: number;
  authParams?: Record<string, string>;
}

interface RequiredEnhancedWebSocketOptions extends EnhancedWebSocketOptions {
  maxReconnectAttempts: number;
  initialReconnectDelay: number;
  maxReconnectDelay: number;
  heartbeatInterval: number;
  heartbeatTimeout: number;
  authParams: Record<string, string>;
  onOpen: () => void;
}

export interface WebSocketManager {
  connect(): void;
  disconnect(): void;
  send(data: string): boolean;
  isConnected(): boolean;
  getReconnectAttempts(): number;
}

export class WebSocketConnectionManager implements WebSocketManager {
  private socket: WebSocket | null = null;
  private options: RequiredEnhancedWebSocketOptions;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private heartbeatIntervalId: NodeJS.Timeout | null = null;
  private heartbeatTimeoutId: NodeJS.Timeout | null = null;
  private isManuallyDisconnected = false;
  private lastPingTime = 0;

  constructor(options: EnhancedWebSocketOptions) {
    this.options = {
      maxReconnectAttempts: 5,
      initialReconnectDelay: 1000,
      maxReconnectDelay: 30000,
      heartbeatInterval: 30000, // 30 seconds
      heartbeatTimeout: 10000, // 10 seconds
      authParams: {},
      onOpen: () => logger.info("WebSocket connected"),
      ...options,
    };
  }

  connect(): void {
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return; // Already connecting or connected
    }

    this.isManuallyDisconnected = false;
    this.createWebSocket();
  }

  private createWebSocket(): void {
    try {
      // Build URL with auth parameters
      const url = new URL(this.options.url);
      Object.entries(this.options.authParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      const finalUrl = url.toString();

      this.socket = new WebSocket(finalUrl);
      this.setupEventHandlers();
    } catch (error) {
      logger.error("[WebSocketManager] Error creating WebSocket:", error);
      this.handleConnectionError(error as Error);
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      logger.info("Enhanced WebSocket connection established");
      this.reconnectAttempts = 0; // Reset on successful connection
      this.options.onConnectionChange(true);
      this.startHeartbeat();
      this.options.onOpen();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      // Handle heartbeat pong messages
      if (this.isHeartbeatMessage(event.data)) {
        this.handleHeartbeatResponse(event.data);
        return;
      }

      this.options.onMessage(event);
    };

    this.socket.onerror = (event: Event) => {
      logger.error("Enhanced WebSocket error", event);
      this.handleConnectionError(new Error("WebSocket connection error"));
    };

    this.socket.onclose = (event: CloseEvent) => {
      logger.info(
        `Enhanced WebSocket connection closed: ${event.code} - ${event.reason}`,
      );
      this.cleanup();
      this.options.onConnectionChange(false);

      // Only attempt reconnection if not manually disconnected and not a normal closure
      if (!this.isManuallyDisconnected && event.code !== 1000) {
        this.scheduleReconnection();
      }
    };
  }

  private isHeartbeatMessage(data: string): boolean {
    try {
      const message = JSON.parse(data);
      return message.type === "pong";
    } catch {
      return false;
    }
  }

  private handleHeartbeatResponse(data: string): void {
    try {
      const message = JSON.parse(data);
      if (message.type === "pong") {
        // Clear heartbeat timeout - server is responding
        if (this.heartbeatTimeoutId) {
          clearTimeout(this.heartbeatTimeoutId);
          this.heartbeatTimeoutId = null;
        }
      }
    } catch (error) {
      logger.error("Error parsing heartbeat response", error);
    }
  }

  /**
   * Initializes the heartbeat mechanism by setting up a periodic ping interval.
   * Clears any existing heartbeat to prevent duplicate intervals. The heartbeat
   * sends ping messages at configured intervals to detect connection health.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat

    this.heartbeatIntervalId = setInterval(() => {
      this.sendHeartbeat();
    }, this.options.heartbeatInterval);
  }

  private sendHeartbeat(): void {
    if (this.isConnected()) {
      try {
        this.lastPingTime = Date.now();
        this.socket!.send(
          JSON.stringify({ type: "ping", timestamp: this.lastPingTime }),
        );

        // Set timeout for pong response
        this.heartbeatTimeoutId = setTimeout(() => {
          logger.warn("Heartbeat timeout - no pong received");
          this.handleHeartbeatTimeout();
        }, this.options.heartbeatTimeout);
      } catch (error) {
        logger.error("Error sending heartbeat", error);
        this.handleConnectionError(error as Error);
      }
    }
  }

  /**
   * Handles the scenario where a 'pong' message is not received within the configured
   * heartbeat timeout period. Assumes the connection is dead and closes it with code 1006
   * to trigger the reconnection logic. Sets isManuallyDisconnected to false to allow
   * automatic reconnection attempts.
   */
  private handleHeartbeatTimeout(): void {
    logger.error("Heartbeat timeout - closing connection");
    this.isManuallyDisconnected = false; // Allow reconnection
    this.socket?.close(1006, "Heartbeat timeout");
  }

  private stopHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
  }

  /**
   * Schedules a reconnection attempt using exponential backoff strategy with jitter.
   * If the maximum number of reconnection attempts has been reached, calls the
   * onError callback and stops trying to reconnect.
   *
   * Uses exponential backoff: delay = initialDelay * 2^attempts, capped at maxDelay.
   * Adds ±25% jitter to prevent thundering herd effect when multiple clients reconnect.
   */
  private scheduleReconnection(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      const error = new Error(
        `Max reconnection attempts reached (${this.options.maxReconnectAttempts}). Giving up.`,
      );
      this.options.onError(error);
      return;
    }

    // Exponential backoff with jitter
    const baseDelay = Math.min(
      this.options.initialReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.options.maxReconnectDelay,
    );

    // Add jitter (±25% of the delay)
    const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
    const delay = baseDelay + jitter;

    logger.info(
      `Scheduling reconnection attempt ${this.reconnectAttempts + 1} in ${Math.round(delay)}ms`,
    );

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectAttempts++;
      this.createWebSocket();
    }, delay);
  }

  private handleConnectionError(error: Error): void {
    logger.error("Enhanced WebSocket connection error", error);
  }

  private cleanup(): void {
    this.stopHeartbeat();

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  disconnect(): void {
    this.isManuallyDisconnected = true;
    this.cleanup();

    if (this.socket) {
      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close(1000, "Manual disconnect");
      }
      this.socket = null;
    }

    this.options.onConnectionChange(false);
  }

  send(data: string): boolean {
    if (this.isConnected()) {
      try {
        this.socket!.send(data);
        return true;
      } catch (error) {
        logger.error("Error sending WebSocket message", error);
        this.handleConnectionError(error as Error);
        return false;
      }
    }

    logger.warn("Cannot send message: WebSocket is not connected");
    return false;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // Get connection state for debugging
  getConnectionInfo(): {
    state: string;
    reconnectAttempts: number;
    isManuallyDisconnected: boolean;
    hasHeartbeat: boolean;
  } {
    const states = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"];
    return {
      state: this.socket ? states[this.socket.readyState] : "DISCONNECTED",
      reconnectAttempts: this.reconnectAttempts,
      isManuallyDisconnected: this.isManuallyDisconnected,
      hasHeartbeat: this.heartbeatIntervalId !== null,
    };
  }
}

/**
 * Creates an enhanced WebSocket connection with auto-reconnection, heartbeat, and proper error handling
 * This is the main factory function to create WebSocket managers for components
 */
export function createEnhancedWebSocket(
  options: EnhancedWebSocketOptions,
): WebSocketManager {
  const manager = new WebSocketConnectionManager(options);
  manager.connect(); // Auto-connect on creation
  return manager;
}

/**
 * Helper function to create WebSocket connection for QR code modal with auth parameters
 */
export function createQRCodeWebSocket(
  baseUrl: string,
  companyName: string,
  onMessage: (event: MessageEvent) => void,
  onError: (error: Error) => void,
  onConnectionChange: (connected: boolean) => void,
): WebSocketManager {
  //  Corrected WebSocket endpoint for QR Code (backend: /api/ws in connection.go)
  const url = new URL("/api/ws", baseUrl);

  return createEnhancedWebSocket({
    url: url.toString(),
    onMessage,
    onError,
    onConnectionChange,
    authParams: {
      companyName: companyName || "unknown",
    },
    maxReconnectAttempts: 3, // More conservative for QR code use case
    initialReconnectDelay: 2000, // Slightly longer initial delay
    heartbeatInterval: 30000, // 30 seconds
    heartbeatTimeout: 10000, // 10 seconds
  });
}
